import React, { useCallback, useState, useContext, createContext, useEffect } from "react";
import PropTypes from "prop-types";
import { useInterval } from "../../hooks";
import useGatsbyAuth from "../../templates/user-account/hook/useGatsbyAuth";
import { updateProfileMapping, getProfile } from "../../utils/Account/helper";

const UserContext = createContext({
	user: null,
	isLoggedIn: false,
	affiliate: {
		code: null,
		name: null,
	},
	isRegistrationRedirect: false,
	profileTab: null,
	profile: undefined,
	isAgent: false,
});

const useUser = () => useContext(UserContext);

const UserProvider = ({ children }) => {
	const { loading: authServiceLoading, authState, authService } = useGatsbyAuth();
	const [loading, setLoading] = useState(true);
	const [user, setUser] = useState();
	const [affiliate, setAffiliate] = useState({
		code: "wes",
		name: null,
	});
	const [isLoggedIn, setIsLoggedIn] = useState(false);
	const [isRegistrationRedirect, setIsRegistrationRedirect] = useState(true);
	const [profileTab, setProfileTab] = useState();
	const [profile, setProfile] = useState();
	const [isLoadingProfile, setIsLoadingProfile] = useState();
	const [isAgent, setIsAgent] = useState(false);

	const updateUser = async _user => {
		setLoading(false);
		setUser(_user);

		if (!authServiceLoading) {
			if (_user && authState && authState.isAuthenticated) {
				if (profile === undefined && !isLoadingProfile) {
					setIsLoadingProfile(true);
					await getProfile(authState)
						.then(response => {
							setProfile(updateProfileMapping(response));
							setIsLoadingProfile(false);

							setIsAgent(Boolean(response?.contactType === "AGENT"));
						})
						.catch(err => {
							console.error("getProfileData API: error: ", err);
							setIsLoadingProfile(false);
						});
				}
				setIsLoggedIn(true);
			} else {
				setIsLoggedIn(false);
				authService.tokenManager.clear();
			}
		}
	};

	const refreshSession = useCallback(async () => {
		if (document.visibilityState === "visible") {
			await authService.session
				.get()
				.then(session => {
					// If the session is valid & active, get the user data and update the user
					if (session.status === "ACTIVE") {
						session.user().then(async sessionUser => {
							updateUser(sessionUser);
						});
					} else if (document.visibilityState === "visible") {
						updateUser();
					}
				})
				.catch(() => {
					// If no session, clear the user.
					if (document.visibilityState === "visible") {
						updateUser();
					}
				});
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authState]);

	useEffect(() => {
		// On load
		if (!authServiceLoading && authState) {
			(async () => {
				setIsAgent(Boolean(authState?.idToken?.claims?.agentGlobalId));
				await refreshSession();
			})();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authServiceLoading, authState]);

	useEffect(() => {
		// Check the session on window focus
		window.addEventListener("visibilitychange", refreshSession);

		return () => {
			window.removeEventListener("visibilitychange", refreshSession);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Check the session and update the authState every 5 minutes
	useInterval(refreshSession, 5 * 60 * 1000);

	const value = {
		loading,
		isLoggedIn,
		setIsLoggedIn,
		user,
		updateUser,
		authState,
		authService,
		affiliate,
		setAffiliate,
		isRegistrationRedirect,
		setIsRegistrationRedirect,
		refreshSession,
		profileTab,
		setProfileTab,
		profile,
		setProfile,
		isAgent,
	};

	return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

UserProvider.propTypes = {
	children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
};

export { UserProvider, useUser };
